import {makeAutoObservable, observable} from 'mobx'
import {DragEvent} from 'react'
import {Lang} from 'yougile-lang'
import {Checklist, DataObjectId, getChecklists, Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {DragData} from '@common/drag-data'
import {createUseStore} from '@containers/hooks'
import {Task} from '@extensions/task'
import {
  deletePlaceholderFlag,
  isPlaceholder,
  toPlaceholder
} from './placeholder'
import {ItemFullId} from './types'

interface State {
  checklists: Checklist[]
  allowChecklists: boolean
  newListId?: DataObjectId
  editInProgress?: boolean
  waitForRender: boolean
}

export class ChecklistsStore {
  state: State = {
    checklists: [],
    allowChecklists: true,
    waitForRender: true
  }

  hub: Hub
  dragStartListId?: DataObjectId

  constructor() {
    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
  }

  syncOnNew(hub: Hub): void {
    this.hub = hub
    this.syncOnUpdate()
  }

  syncOnUpdate(): void {
    this.state.checklists = [...getChecklists(this.hub)]

    const isTaskTemplate = Task.isTaskTemplate(this.hub)

    this.state.allowChecklists =
      !!this.hub &&
      (isTaskTemplate || PermissionsChecker.checkEditSubtasks(this.hub))
  }

  add(): void {
    const isTaskTemplate = Task.isTaskTemplate(this.hub)
    if (!isTaskTemplate && !PermissionsChecker.checkEditSubtasks(this.hub)) {
      return
    }
    this.state.newListId = Task.addChecklist(
      this.hub,
      Lang.msg.checklistDefaultName,
      true
    )
  }

  setNewListProcessed(): void {
    this.state.newListId = undefined
  }

  updateEditInProgress(editInProgress: boolean): void {
    this.state.editInProgress = editInProgress
  }

  rerender(): void {
    this.state.waitForRender = false
  }

  onDragStart(listId: DataObjectId, e: React.DragEvent): void {
    const list = this.state.checklists.find((c: Checklist) => c.id === listId)
    DragData.set(e, {type: 'checklist', list, taskId: this.hub.id})
  }

  onDragEnd(): void {
    Task.setChecklists(this.hub, this.state.checklists)
  }

  onDrag(id: DataObjectId, to: DataObjectId): void {
    if (!id || !to || id === to) {
      return
    }

    const lists = this.state.checklists
    const ixDrag = lists.findIndex((c: Checklist) => c.id === id)
    const ixOver = lists.findIndex((c: Checklist) => c.id === to)
    if (ixDrag !== -1 && ixOver !== -1) {
      const dragged = lists[ixDrag]
      lists.splice(ixDrag, 1)
      lists.splice(ixOver, 0, dragged)
    }
  }

  onDragItemStart(itemId: ItemFullId): void {
    this.dragStartListId = itemId.listId

    const draggedList = this.state.checklists.find(c => c.id === itemId.listId)
    const ixDraggedItem = draggedList.items.findIndex(
      c => c.id === itemId.itemId
    )
    draggedList.items.splice(
      ixDraggedItem,
      0,
      toPlaceholder(draggedList.items[ixDraggedItem])
    )
  }

  onDragItemEnd(draggedId: ItemFullId): void {
    const lists = this.state.checklists
    const draggedList = lists.find(c => c.id === draggedId.listId)
    const ixDraggedItemPlaceholder = draggedList.items.findIndex(
      c => c.id === draggedId.itemId && isPlaceholder(c)
    )
    if (ixDraggedItemPlaceholder < 0) {
      return
    }
    const initDraggedList = lists.find(c => c.id === this.dragStartListId)
    const ixDraggedItem = initDraggedList.items.findIndex(
      c => c.id === draggedId.itemId && !isPlaceholder(c)
    )
    if (ixDraggedItem < 0) {
      return
    }
    deletePlaceholderFlag(draggedList.items[ixDraggedItemPlaceholder])
    initDraggedList.items.splice(ixDraggedItem, 1)

    Task.setChecklists(this.hub, lists)
  }

  onDragItem(draggedId: ItemFullId, overId: ItemFullId, e: DragEvent): void {
    if (
      !draggedId ||
      !overId ||
      (draggedId.listId === overId.listId &&
        draggedId.itemId === overId.itemId) ||
      this.state.waitForRender
    ) {
      return
    }

    const insertToList = overId.itemId === undefined
    const lists = this.state.checklists
    const draggedList = lists.find(c => c.id === draggedId.listId)
    const overList = lists.find(c => c.id === overId.listId)
    if (!draggedList || !overList) {
      return
    }
    let ixDraggedItem = draggedList.items.findIndex(
      c => c.id === draggedId.itemId && isPlaceholder(c)
    )
    let ixOverItem = overList.items.findIndex(c => c.id === overId.itemId)
    if (ixDraggedItem < 0) {
      ixDraggedItem = draggedList.items.findIndex(
        c => c.id === draggedId.itemId
      )
      if (ixDraggedItem < 0) {
        return
      }
      if (ixOverItem < 0) {
        ixOverItem = ixDraggedItem
      }
    }

    if (!insertToList) {
      if (ixOverItem < 0) {
        return
      }
      if (
        ixOverItem > 0 &&
        !this.isDraggedReachedMidLine(draggedId, overId, e)
      ) {
        return
      }
    }

    const dragged = draggedList.items[ixDraggedItem]
    draggedList.items.splice(ixDraggedItem, 1)
    if (insertToList) {
      overList.items.push(toPlaceholder(dragged))
    } else {
      overList.items.splice(ixOverItem, 0, toPlaceholder(dragged))
    }
    draggedId.listId = overList.id
    this.state.waitForRender = true
  }

  private isDraggedReachedMidLine(
    draggedId: ItemFullId,
    overId: ItemFullId,
    e: DragEvent
  ): boolean {
    const draggedEl = document.getElementById(
      'chat-checklist-item-' + draggedId.itemId
    )
    const overEl = document.getElementById(
      'chat-checklist-item-' + overId.itemId
    )
    if (!overEl || !draggedEl) {
      return true
    }
    const draggedRect = draggedEl.getBoundingClientRect()
    const overRect = overEl.getBoundingClientRect()
    const top = Math.min(overRect.top, draggedRect.top)
    const bottom = Math.max(overRect.bottom, draggedRect.bottom)
    const mid = (top + bottom) / 2
    const isDraggedHigher = draggedRect.top < overRect.top
    const isMouseHigher = e.clientY < mid
    return isDraggedHigher !== isMouseHigher
  }
}

export const [ChecklistsContext, useChecklistsStore] =
  createUseStore(ChecklistsStore)
